import React, { useRef, useMemo, useState } from 'react'
import { Printer, Download, X, Delete, CheckCircle2 } from 'lucide-react'
import { CartItem } from '@/types/pos'
import { getLineTotalForItem } from '@/store/posStore'

export interface AdvancePaymentModalProps {
  cart: CartItem[]
  subtotal: number
  discount: number
  discountType: 'percentage' | 'fixed'
  tax: number
  total: number
  customerName?: string | null
  customerId?: string | null
  customerPhone?: string | null
  storeName?: string
  storeAddress?: string
  storePhone?: string
  storeEmail?: string
  onRecordAdvance?: (params: { requestId: string; amount: number; customerPhone?: string }) => void
  onClose: () => void
}

const ADVANCE_FOOTER = 'Please pay the above amount before delivery. Thank you!'

export const AdvancePaymentModal: React.FC<AdvancePaymentModalProps> = ({
  cart,
  subtotal,
  discount,
  discountType,
  tax,
  total,
  customerName,
  customerPhone: initialPhone,
  storeName = 'Sachin Poth Madura',
  storeAddress = '',
  storePhone = '',
  storeEmail = '',
  onRecordAdvance,
  onClose,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null)
  const [advanceAmountPaid, setAdvanceAmountPaid] = useState('')
  const [phoneNumber, setPhoneNumber] = useState(initialPhone || '')

  const advanceNum = Math.max(0, Math.min(total, parseFloat(advanceAmountPaid) || 0))
  const balanceDue = total - advanceNum

  const referenceNumber = useMemo(
    () => `ADV-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(Date.now()).slice(-6)}`,
    []
  )

  const saleItems = useMemo(() => cart.filter(i => !i.isReturn), [cart])
  const discountAmount = useMemo(() => {
    const positiveSubtotal = Math.max(0, subtotal)
    return discountType === 'percentage'
      ? (positiveSubtotal * discount) / 100
      : Math.min(discount, positiveSubtotal)
  }, [subtotal, discount, discountType])

  const keypadKeys = [
    ['7', '8', '9'],
    ['4', '5', '6'],
    ['1', '2', '3'],
    ['0', '00', '.'],
  ]

  const handleKeypadPress = (key: string) => {
    if (key === 'clear') {
      setAdvanceAmountPaid('')
    } else if (key === 'backspace') {
      setAdvanceAmountPaid(prev => prev.slice(0, -1))
    } else if (key === '.') {
      if (!advanceAmountPaid.includes('.')) setAdvanceAmountPaid(prev => prev + '.')
    } else if (key === '00') {
      if (advanceAmountPaid && !advanceAmountPaid.includes('.')) setAdvanceAmountPaid(prev => prev + '00')
    } else {
      const parts = advanceAmountPaid.split('.')
      if (parts.length === 2 && parts[1].length >= 2) return
      setAdvanceAmountPaid(prev => prev + key)
    }
  }

  const handleRecordAdvance = () => {
    if (advanceNum <= 0) return
    onRecordAdvance?.({ requestId: referenceNumber, amount: advanceNum, customerPhone: phoneNumber || undefined })
    onClose()
  }

  const handlePrint = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow || !receiptRef.current) return

    const itemsHtml = saleItems
      .map(
        item => {
          const lineTotal = getLineTotalForItem(item)
          return `
          <div class="item">
            <span class="item-name">${item.product.name || 'Item'}</span>
            <span class="item-qty">x${item.quantity}</span>
            <span class="item-price">Rs.${lineTotal.toFixed(2)}</span>
          </div>`
        }
      )
      .join('')

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Advance Payment Request - ${referenceNumber}</title>
          <style>
            @media print {
              @page { margin: 2mm 5mm; size: 80mm auto; }
              body { margin: 0; padding: 4mm 6mm; }
            }
            body {
              font-family: 'Arial', sans-serif;
              font-size: 12px;
              line-height: 1.4;
              max-width: 80mm;
              margin: 0 auto;
              padding: 4mm 6mm;
              color: #000;
            }
            .header { text-align: center; margin-bottom: 16px; }
            .header h1 { margin: 0; font-size: 18px; font-weight: bold; }
            .header .advance-badge { background: #1e40af; color: #fff; padding: 4px 12px; border-radius: 4px; font-size: 11px; margin-top: 8px; display: inline-block; }
            .header p { margin: 4px 0; font-size: 10px; }
            .divider { border-top: 1px dashed #000; margin: 12px 0; }
            .item { display: flex; justify-content: space-between; margin: 6px 0; font-size: 11px; }
            .item-name { flex: 1; max-width: 60%; }
            .item-qty { margin: 0 8px; }
            .item-price { text-align: right; font-weight: bold; }
            .totals { margin-top: 12px; }
            .total-row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 11px; }
            .total-row.final { font-weight: bold; font-size: 14px; border-top: 2px solid #000; padding-top: 8px; margin-top: 8px; }
            .footer { text-align: center; margin-top: 20px; font-size: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${storeName}</h1>
            <span class="advance-badge">ADVANCE PAYMENT REQUEST</span>
            ${storeAddress ? `<p>${storeAddress}</p>` : ''}
            ${storePhone ? `<p>Tel: ${storePhone}</p>` : ''}
            ${storeEmail ? `<p>Email: ${storeEmail}</p>` : ''}
            <p>${new Date().toLocaleString()}</p>
          </div>
          <div class="divider"></div>
          <div>
            <p><strong>Request #:</strong> ${referenceNumber}</p>
            <p><strong>Date:</strong> ${new Date().toLocaleString()}</p>
            ${customerName ? `<p><strong>Customer:</strong> ${customerName}</p>` : ''}
            ${phoneNumber ? `<p><strong>Phone:</strong> ${phoneNumber}</p>` : ''}
          </div>
          <div class="divider"></div>
          <div>${itemsHtml}</div>
          <div class="divider"></div>
          <div class="totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>Rs.${subtotal.toFixed(2)}</span>
            </div>
            ${discountAmount > 0 ? `
              <div class="total-row">
                <span>Discount${discountType === 'percentage' ? ` (${discount}%)` : ''}:</span>
                <span>-Rs.${discountAmount.toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="total-row">
              <span>Tax:</span>
              <span>Rs.${tax.toFixed(2)}</span>
            </div>
            <div class="total-row final">
              <span>AMOUNT DUE:</span>
              <span>Rs.${total.toFixed(2)}</span>
            </div>
            ${advanceNum > 0 ? `
              <div class="total-row" style="color:#059669;">
                <span>Advance paid:</span>
                <span>Rs.${advanceNum.toFixed(2)}</span>
              </div>
              <div class="total-row final">
                <span>BALANCE DUE:</span>
                <span>Rs.${balanceDue.toFixed(2)}</span>
              </div>
            ` : ''}
          </div>
          <div class="divider"></div>
          <div class="footer">
            <p>${ADVANCE_FOOTER}</p>
          </div>
        </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 250)
  }

  const handleDownload = () => {
    const lines = saleItems.map(item => {
      const lineTotal = getLineTotalForItem(item)
      return `${(item.product.name || 'Item').padEnd(25)} x${item.quantity}  Rs.${lineTotal.toFixed(2)}`
    }).join('\n')

    const content = `
${storeName}
${storeAddress}
${storePhone ? `Tel: ${storePhone}` : ''}
${storeEmail ? `Email: ${storeEmail}` : ''}

ADVANCE PAYMENT REQUEST
Request #: ${referenceNumber}
Date: ${new Date().toLocaleString()}
${customerName ? `Customer: ${customerName}` : ''}
${phoneNumber ? `Phone: ${phoneNumber}` : ''}

${'─'.repeat(40)}

${lines}

${'─'.repeat(40)}

Subtotal: Rs.${subtotal.toFixed(2)}
${discountAmount > 0 ? `Discount: -Rs.${discountAmount.toFixed(2)}\n` : ''}Tax: Rs.${tax.toFixed(2)}
${'═'.repeat(40)}
AMOUNT DUE: Rs.${total.toFixed(2)}
${advanceNum > 0 ? `Advance paid: Rs.${advanceNum.toFixed(2)}\nBALANCE DUE: Rs.${balanceDue.toFixed(2)}\n` : ''}

${'─'.repeat(40)}

${ADVANCE_FOOTER}
    `.trim()

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `advance-payment-${referenceNumber}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold esales-text">Advance Payment Request</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-muted-foreground text-sm mb-4">
          Request partial or full payment before delivery. Use the advance receipt ID at checkout to apply it to a bill.
        </p>

        {/* Receipt-style preview - same layout as default billing receipt */}
        <div ref={receiptRef} className="bg-white text-black p-6 rounded-lg text-sm mb-6 shadow-inner" style={{ fontFamily: 'Arial, sans-serif' }}>
          <div className="text-center mb-4">
            <h4 className="font-bold text-lg">{storeName}</h4>
            <span className="inline-block mt-2 px-3 py-1 bg-blue-600 text-white text-[10px] font-bold rounded uppercase tracking-wide">
              Advance Payment Request
            </span>
            {storeAddress && <p className="text-xs mt-2">{storeAddress}</p>}
            {storePhone && <p className="text-xs">Tel: {storePhone}</p>}
            {storeEmail && <p className="text-xs">Email: {storeEmail}</p>}
            <p className="text-xs mt-2">{new Date().toLocaleString()}</p>
          </div>

          <div className="border-t border-b border-gray-300 py-2 my-3">
            <p className="text-xs"><strong>Request #:</strong> {referenceNumber}</p>
            {customerName && <p className="text-xs"><strong>Customer:</strong> {customerName}</p>}
            {phoneNumber && <p className="text-xs"><strong>Phone:</strong> {phoneNumber}</p>}
          </div>

          <div className="my-4 space-y-1">
            {saleItems.map((item, index) => {
              const lineTotal = getLineTotalForItem(item)
              return (
                <div key={`adv-${index}`} className="flex justify-between text-xs">
                  <span className="flex-1 truncate pr-2">{item.product.name || 'Item'}</span>
                  <span className="px-2">x{item.quantity}</span>
                  <span className="font-bold">Rs.{lineTotal.toFixed(2)}</span>
                </div>
              )
            })}
          </div>

          <div className="border-t border-gray-300 pt-3 mt-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span>Subtotal:</span>
              <span>Rs.{subtotal.toFixed(2)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-xs text-green-600">
                <span>Discount{discountType === 'percentage' ? ` (${discount}%)` : ''}:</span>
                <span>-Rs.{discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs">
              <span>Tax:</span>
              <span>Rs.{tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-base border-t border-gray-300 pt-2 mt-2">
              <span>AMOUNT DUE:</span>
              <span>Rs.{total.toFixed(2)}</span>
            </div>
            {advanceNum > 0 && (
              <>
                <div className="flex justify-between text-xs text-green-600 mt-2">
                  <span>Advance paid:</span>
                  <span>Rs.{advanceNum.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-base border-t border-gray-300 pt-2 mt-2">
                  <span>BALANCE DUE:</span>
                  <span>Rs.{balanceDue.toFixed(2)}</span>
                </div>
              </>
            )}
          </div>

          <div className="text-center mt-6 border-t border-gray-300 pt-3">
            <p className="text-xs">{ADVANCE_FOOTER}</p>
          </div>
        </div>

        {/* Customer phone number (required for guest lookup) */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-muted-foreground mb-1">Customer Phone Number</p>
          <p className="text-xs text-muted-foreground mb-2">Required for guest customers to look up their advance later.</p>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9+\- ]/g, ''))}
            placeholder="e.g. 077 123 4567"
            className="w-full px-4 py-3 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none"
          />
        </div>

        {/* Advance amount keypad */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-muted-foreground mb-2">Amount customer paid as advance (Rs.)</p>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg text-muted-foreground">Rs.</span>
            <input
              type="text"
              value={advanceAmountPaid}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '')
                const parts = val.split('.')
                if (parts.length > 2) return
                if (parts.length === 2 && parts[1].length > 2) return
                setAdvanceAmountPaid(val)
              }}
              placeholder="0.00"
              className="flex-1 px-4 py-3 bg-background border-2 border-border rounded-xl text-right font-mono text-xl font-bold focus:border-primary focus:outline-none"
            />
          </div>
          <div className="flex gap-2 mb-2">
            <button type="button" onClick={() => setAdvanceAmountPaid(total.toFixed(2))} className="flex-1 py-2 bg-primary/20 text-primary hover:bg-primary/30 rounded-lg text-sm font-bold">
              Full amount
            </button>
            <button type="button" onClick={() => handleKeypadPress('clear')} className="py-2 px-3 bg-muted hover:bg-muted/80 rounded-lg text-sm font-bold flex items-center justify-center gap-1">
              <X className="w-4 h-4" /> Clear
            </button>
            <button type="button" onClick={() => handleKeypadPress('backspace')} className="py-2 px-3 bg-red-500/20 text-red-500 hover:bg-red-500/30 rounded-lg text-sm font-bold flex items-center justify-center gap-1">
              <Delete className="w-4 h-4" /> Del
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {keypadKeys.flat().map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => handleKeypadPress(key)}
                className="py-3 bg-muted hover:bg-muted/80 rounded-xl text-lg font-bold transition-colors active:scale-95"
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        {/* Record Advance then Print / Download / Close */}
        {advanceNum > 0 && (
          <button
            type="button"
            onClick={handleRecordAdvance}
            className="w-full py-3 mb-4 rounded-xl font-bold bg-green-600 text-white hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-5 h-5" />
            Record advance (Rs.{advanceNum.toFixed(2)}) &amp; close
          </button>
        )}

        {/* Actions - same 3-column layout as ReceiptModal (Print, Download, Close) */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <button
            onClick={handlePrint}
            className="flex flex-col items-center justify-center p-4 bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors"
          >
            <Printer className="w-6 h-6 text-primary mb-1" />
            <span className="text-xs font-semibold">Print</span>
          </button>
          <button
            onClick={handleDownload}
            className="flex flex-col items-center justify-center p-4 bg-blue-500/10 hover:bg-blue-500/20 rounded-xl transition-colors"
          >
            <Download className="w-6 h-6 text-blue-500 mb-1" />
            <span className="text-xs font-semibold">Download</span>
          </button>
          <button
            onClick={onClose}
            className="flex flex-col items-center justify-center p-4 bg-muted hover:bg-muted/80 rounded-xl transition-colors"
          >
            <X className="w-6 h-6 text-muted-foreground mb-1" />
            <span className="text-xs font-semibold">Close</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdvancePaymentModal
